/**
 * 起動中オーバーレイ。
 * ピクセルグリッドの波 + シマーラベル + 0–100% の mono 表示。
 * 固まったときにアプリを閉じなくて済むよう、キャンセルを必ず出す。
 */

const chevron = Array.from({ length: 9 }, (_, i) => {
  const r = Math.floor(i / 3);
  const c = i % 3;
  return (c + Math.abs(r - 1)) * 90;
});

function LoaderGrid() {
  return (
    <span aria-hidden className="grid shrink-0 grid-cols-[repeat(3,4px)] gap-[1.5px]">
      {chevron.map((delay, index) => (
        <span
          key={index}
          className="load-pixel size-1 rounded-[1px] bg-slate-200"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );
}

export interface LoadProgressOverlayProps {
  title: string;
  /** 省略時は不定進捗（バーが流れる） */
  percent?: number;
  label: string;
  onCancel?: () => void;
}

export function LoadProgressOverlay({ title, percent, label, onCancel }: LoadProgressOverlayProps) {
  const indeterminate = percent === undefined;
  const shown = indeterminate ? 0 : Math.max(0, Math.min(100, Math.round(percent)));

  return (
    <div
      className="absolute inset-0 z-30 flex items-center justify-center bg-black/85 px-6"
      role="status"
      aria-live="polite"
      aria-busy="true"
      {...(indeterminate
        ? {}
        : {
            'aria-valuemin': 0,
            'aria-valuemax': 100,
            'aria-valuenow': shown,
          })}
      aria-label={
        indeterminate ? `${title} を読み込み中` : `${title} を読み込み中 ${shown}%`
      }
    >
      <div className="flex w-full max-w-sm flex-col items-center gap-4">
        <div className="flex items-center gap-2.5">
          <LoaderGrid />
          <span className="load-shimmer text-[13px] font-medium">{label || '読み込み中'}</span>
          {!indeterminate && (
            <span className="font-mono text-[12px] tabular-nums text-slate-400">{shown}%</span>
          )}
        </div>

        <div className="w-full">
          <p className="mb-2 truncate text-center text-xs text-slate-500">{title}</p>
          <div className="h-1.5 overflow-hidden rounded-full bg-base-700">
            {indeterminate ? (
              <div className="load-bar-indeterminate h-full w-1/3 rounded-full bg-accent" />
            ) : (
              <div
                className="h-full rounded-full bg-accent transition-[width] duration-200 ease-out"
                style={{ width: `${shown}%` }}
              />
            )}
          </div>
        </div>

        {onCancel && (
          <button type="button" className="btn-ghost mt-2 !min-h-10 !px-5 !text-sm" onClick={onCancel}>
            キャンセル
          </button>
        )}
      </div>
    </div>
  );
}
